import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';

import { MediaResponse } from '../../../../api-gen/gen/models/media-response';
import { MediaType } from '../../../../api-gen/gen/models/media-type';
import { PromptsManagerResourceService } from '../../../../api-gen/gen-server/services/prompts-manager-resource.service';
import { environment } from '../../../environments/environment';
import { MetaService } from '../../service/meta.service';
import { NewBadgeComponent } from '../../shared-components/new-badge/new-badge.component';
import { MODEL_FAQ, MODEL_SHOWCASES } from './model-overview.data';

const STRUCTURED_DATA_ID = 'model-overview-structured-data';

/** A gallery tile: the media to show and the share slug it links to. */
interface GalleryItem {
  shareId: string;
  media: MediaResponse;
}

@Component({
  selector: 'app-model-overview',
  imports: [
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    NewBadgeComponent,
  ],
  templateUrl: './model-overview.component.html',
  styleUrl: './model-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelOverviewComponent implements OnInit, OnDestroy {
  private readonly metaService = inject(MetaService);
  private readonly document = inject(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly promptsApi = inject(PromptsManagerResourceService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly MediaType = MediaType;
  protected readonly showcases = MODEL_SHOWCASES;
  protected readonly faq = MODEL_FAQ;
  protected readonly siteName = environment.name;

  /** Example gallery items (media + share slug) per model, keyed by slug. */
  protected readonly galleryByModel = signal<
    Record<string, GalleryItem[] | undefined>
  >({});

  ngOnInit(): void {
    this.metaService.update({
      title: `Compare AI Models - Image & Video Generators | ${environment.domain}`,
      description: `Compare every AI model on ${environment.name}: photorealistic, anime, social-media and creative generators. See example images and pick the best AI model for your next creation.`,
      keywords:
        'compare AI models, AI model comparison, AI image generator, realistic AI model, anime AI model, AI influencer generator, best AI model',
    });

    // MetaService prepends the canonical origin; pass the route path only.
    // (SSR-safe: no window/location access.)
    this.metaService.updateCanonicalUrl('/ai-model-overview');

    this.loadExampleMedia();
    this.injectStructuredData();
  }

  /**
   * When arriving from another page (e.g. the landing page model cards) the
   * scroll container keeps the previous page's position and the router does not
   * honour the URL fragment. Reset to the top and then scroll to the selected
   * model section ourselves.
   */
  constructor() {
    // Scrolling is browser-only (was ngAfterViewInit): server DOM elements
    // don't implement scrollTo/scrollIntoView.
    afterNextRender(() => {
      const fragment = this.route.snapshot.fragment;

      // The app scrolls inside `mat-sidenav-content`, not the window.
      const scrollContainer = this.document.querySelector(
        'mat-sidenav-content',
      );
      scrollContainer?.scrollTo({ top: 0 });

      if (fragment) {
        // Defer so the section element is laid out before we scroll to it.
        setTimeout(() => {
          this.document
            .getElementById(fragment)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.metaService.reset();
    this.document.getElementById(STRUCTURED_DATA_ID)?.remove();
  }

  /** Fetch example gallery media for each model in one batched request. */
  private loadExampleMedia(): void {
    for (const showcase of this.showcases) {
      if (!showcase.shareIds.length) {
        continue;
      }
      this.promptsApi
        .promptsSharedGet({ shareSlug: showcase.shareIds })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((containers) => {
          // The response preserves request order, so zip each container's
          // media back to the share slug we asked for at the same index.
          const items = showcase.shareIds
            .map((shareId, index): GalleryItem | null => {
              const media = containers[index]?.medias?.[0];
              return media ? { shareId, media } : null;
            })
            .filter((item): item is GalleryItem => item !== null);
          this.galleryByModel.update((current) => ({
            ...current,
            [showcase.slug]: items,
          }));
        });
    }
  }

  /**
   * Smooth-scroll to an in-page section. We prevent the default anchor
   * behaviour so the URL fragment change does not trigger a router
   * navigation back to the home route, while keeping the `href` intact
   * for SEO and accessibility.
   */
  protected scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Inject JSON-LD ItemList + FAQPage so search engines can index the models. */
  private injectStructuredData(): void {
    // Environment-based origin: correct during SSR/prerendering (no window)
    // and always points at the canonical domain.
    const origin = environment.canonicalOrigin;

    const structuredData = [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${environment.name} AI Models`,
        description: `The complete collection of AI image and video models available on ${environment.name}.`,
        itemListElement: this.showcases.map((showcase, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: showcase.option.name,
          description: showcase.longDescription,
          url: `${origin}/ai-model-overview#${showcase.slug}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ];

    this.document.getElementById(STRUCTURED_DATA_ID)?.remove();
    const script = this.document.createElement('script');
    script.id = STRUCTURED_DATA_ID;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    this.document.head.appendChild(script);
  }
}
